import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaWallet,
  FaShareAlt,
  FaProductHunt,
  FaChartLine,
  FaGift,
  FaCar,
  FaCrown,
  FaUsers,
  FaStar,
  FaRocket,
  FaStore,
  FaTruck,
  FaAward,
  FaGem,
  FaExchangeAlt,
  FaMobile,
  FaDesktop,
  FaGlobe,
  FaTrophy,
  FaRupeeSign,
  FaPercent,
  FaCheckCircle,
  FaUserTie,
  FaBuilding,
  FaHeadset,
  FaShieldAlt,
  FaChartBar,
  FaTree,
  FaInfinity,
  FaShoppingCart, FaPlane,
  FaHandHoldingUsd,
  FaCoins,
  FaUniversity,
  FaQrcode,
  FaArrowRight,
  FaPlayCircle,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaLock,
  FaHistory,
  FaMoneyBillWave,
  FaUserCheck,
  FaNetworkWired,
  FaMedal,
  FaMapMarkedAlt,
  FaBoxOpen,
  FaFileInvoice,
  FaQrcode as FaQrCode,
  FaGooglePlay,
  FaApple,
  FaWindows,
  FaCloudUploadAlt
} from "react-icons/fa";
// Rank images
import RankStarter from "../assets/starter.jpeg";
import RankBuilder from "../assets/builder.jpeg";
import RankAchiever from "../assets/achiever.jpeg";
import RankPerformer from "../assets/performer.jpeg";
import RankGrower from "../assets/grower.jpeg";
import RankLeader from "../assets/leader.jpeg";
import RankStarLeader from "../assets/starleader.jpeg";
import RankSilverIcon from "../assets/silvericon.jpeg";
import RankGoldIcon from "../assets/goldicon.jpeg";
import RankPlatinumIcon from "../assets/platinumicon.jpeg";
import RankDiamondLeader from "../assets/diamondleader.jpeg";
import RankCrownDiamond from "../assets/crowndiamond.jpeg";
import RankRoyalCrown from "../assets/royalcrown.jpeg";
import RankGlobalCrown from "../assets/globalcrown.jpeg";
import RankEliteRoyal from "../assets/eliteroyal.jpeg";
import RankChairmanClub from "../assets/chairmanclub.jpeg";
import RankLegacyFounder from "../assets/legacyfounder.jpeg";

// Color palette
const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

function Features() {
  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-white mt-16">
      {/* ===== HERO SECTION ===== */}
      <section
        className="pt-32 pb-20 text-white relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.navyBlue} 0%, ${colors.darkBlue} 100%)`
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Platform Features
            </h1>
            <p className="text-xl mb-8" style={{ color: colors.lightOrange }}>
              Everything you need to build a successful MLM business
            </p>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              ABM Group One offers a comprehensive suite of features designed to make your MLM journey smooth, transparent, and profitable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== FEATURE CATEGORIES ===== */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          {/* Income Features */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
                Income Features
              </h2>
              <p className="text-xl" style={{ color: colors.oliveBrown }}>
                Multiple ways to earn and grow
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                {
                  icon: FaShoppingCart,
                  title: "Retail Profit (10% to 25%)",
                  desc: "Earn 10% to 25% margin on direct product sales to retail customers. Instant profit on every sale.",
                  color: colors.teal || '#008080',
                  features: ["Direct customer sales", "Flexible profit margin", "Immediate earnings"]
                },
                {
                  icon: FaStar,
                  title: "Direct Bonus (5%)",
                  desc: "Earn 5% instant commission on every new joining and repurchase by your direct referrals.",
                  color: colors.freshGreen,
                  features: ["Lifetime direct sponsor concept", "Instant payout", "No capping"]
                },
                {
                  icon: FaChartLine,
                  title: "Matrix Income (15%)",
                  desc: "11 levels of matrix income with 3× Infinity matrix system. Earn up to ₹35 lakhs on 1000 BV.",
                  color: colors.brightBlue,
                  features: ["11 levels", "Spillover benefits", "Auto placement"]
                },
                {
                  icon: FaUsers,
                  title: "Generation Income (11%)",
                  desc: "7 rank-based generation commissions from Guide to Visionary ranks. Earn on multiple generations.",
                  color: colors.pinkMagenta,
                  features: ["7 generation ranks", "Team BV based", "Weekly payout"]
                },
                {
                  icon: FaUserCheck,
                  title: "Active Member Reward (5%)",
                  desc: "Extra (5%) reward for maintaining active status with minimum personal and team sales.",
                  color: colors.coral || '#FF7F50',
                  features: ["Monthly activity bonus", "Team performance", "Additional income"]
                },
                {
                  icon: FaPlane,
                  title: "Reward Tour Bonus (3%)",
                  desc: "Earn fully-sponsored domestic and international tour packages based on rank achievements.",
                  color: colors.orange || '#FFA500',
                  features: ["Domestic tours", "International trips", "All expenses paid"]
                },
                {
                  icon: FaCar,
                  title: "Car Bonus (4%)",
                  desc: "Special car bonus for top performers with down payment assistance and completion payment in pool distribution.",
                  color: colors.deepOrange,
                  features: ["5 car ranks", "Down payment support", "Pool distribution"]
                },
                {
                  icon: FaGift,
                  title: "Royalty Bonus (2%)",
                  desc: "Monthly royalty on team performance with 3 royalty ranks. Pool-based distribution on total company volume.",
                  color: '#800080',
                  features: ["Pool based", "Team volume", "Monthly distribution"]
                }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeInUp}
                  className="bg-slate-50 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl text-white mb-4"
                    style={{ backgroundColor: feature.color }}>
                    <feature.icon />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: feature.color }}>{feature.title}</h3>
                  <p className="text-sm mb-4" style={{ color: colors.oliveBrown }}>{feature.desc}</p>
                  <ul className="space-y-2">
                    {feature.features.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <FaCheckCircle style={{ color: feature.color }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Wallet System */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
                3-Tier Wallet System
              </h2>
              <p className="text-xl" style={{ color: colors.oliveBrown }}>
                Secure and transparent wallet management
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* LBBP Wallet */}
              <motion.div variants={fadeInUp} className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4" style={{ borderColor: colors.deepOrange }}>
                <div className="p-6">
                  <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center text-3xl mb-4 mx-auto"
                    style={{ color: colors.deepOrange }}>
                    <FaCoins />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2" style={{ color: colors.darkBrown }}>LBBP Wallet</h3>
                  <p className="text-sm text-center mb-4" style={{ color: colors.oliveBrown }}>Loan-Based Business Partnership Wallet</p>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Growth Capital Partner Income</span>
                      <span className="font-bold" style={{ color: colors.deepOrange }}>✓</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Capital Connector Partner Income</span>
                      <span className="font-bold" style={{ color: colors.deepOrange }}>✓</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Growth Incentive Partner Income</span>
                      <span className="font-bold" style={{ color: colors.deepOrange }}>✓</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-sm font-semibold" style={{ color: colors.deepOrange }}>Min ₹200 for Transfer</p>
                  </div>
                </div>
              </motion.div>

              {/* PU&AP Wallet */}
              <motion.div variants={fadeInUp} className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4" style={{ borderColor: colors.freshGreen }}>
                <div className="p-6">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-3xl mb-4 mx-auto"
                    style={{ color: colors.freshGreen }}>
                    <FaHandHoldingUsd />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2" style={{ color: colors.darkGreen }}>P.U. & A.P. Wallet</h3>
                  <p className="text-sm text-center mb-4" style={{ color: colors.oliveBrown }}>Product User & Advisor Partner</p>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Direct Bonus</span>
                      <span className="font-bold" style={{ color: colors.freshGreen }}>✓</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Matrix Income</span>
                      <span className="font-bold" style={{ color: colors.freshGreen }}>✓</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Generation Income</span>
                      <span className="font-bold" style={{ color: colors.freshGreen }}>✓</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-sm font-semibold" style={{ color: colors.darkGreen }}>Weekly Payment Cycle</p>
                  </div>
                </div>
              </motion.div>

              {/* E-Wallet */}
              <motion.div variants={fadeInUp} className="bg-white rounded-xl shadow-lg overflow-hidden border-t-4" style={{ borderColor: colors.brightBlue }}>
                <div className="p-6">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-3xl mb-4 mx-auto"
                    style={{ color: colors.brightBlue }}>
                    <FaUniversity />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2" style={{ color: colors.darkBlue }}>E-Wallet</h3>
                  <p className="text-sm text-center mb-4" style={{ color: colors.oliveBrown }}>Withdrawal Ready Wallet</p>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Add Fund</span>
                      <span className="font-bold" style={{ color: colors.brightBlue }}>✓</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Transfer</span>
                      <span className="font-bold" style={{ color: colors.brightBlue }}>✓</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Member to Member</span>
                      <span className="font-bold" style={{ color: colors.brightBlue }}>✓</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm font-semibold" style={{ color: colors.darkBlue }}>Round Figure Withdrawal</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-3 bg-slate-100 px-6 py-3 rounded-full">
                <FaExchangeAlt className="text-xl" style={{ color: colors.deepOrange }} />
                <p className="font-medium" style={{ color: colors.nearBlack }}>
                  LBBP & PU&AP → E-Wallet Transfer Mandatory Before Withdrawal
                </p>
                <FaLock className="text-xl" style={{ color: colors.freshGreen }} />
              </div>
            </div>
          </motion.div>

          {/* My Pickup Center & Mobile Store */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
                Distribution Network
              </h2>
              <p className="text-xl" style={{ color: colors.oliveBrown }}>
                My Pickup Center & Mobile Store Features
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* MPC Card */}
              <motion.div variants={fadeInUp} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="h-3" style={{ background: `linear-gradient(90deg, ${colors.deepOrange}, ${colors.burntOrange})` }}></div>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl text-white"
                      style={{ background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})` }}>
                      <FaStore />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold" style={{ color: colors.darkBrown }}>My Pickup Center</h3>
                      {/* <p className="text-lg font-bold" style={{ color: colors.deepOrange }}>3% Commission</p> */}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.deepOrange }}>Bulk Orders</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.deepOrange }}>Stock Management</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.deepOrange }}>GST Invoices</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.deepOrange }}>Low Stock Alert</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.deepOrange }} />
                      <span className="text-sm">Profile & KYC (GST No., PAN, Bank)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.deepOrange }} />
                      <span className="text-sm">Product Purchase from Company</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.deepOrange }} />
                      <span className="text-sm">Supply to Mobile Stores</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.deepOrange }} />
                      <span className="text-sm">Bulk Order + GST Invoice Download</span>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg">
                    <p className="text-sm font-bold text-center" style={{ color: colors.deepOrange }}>
                      Bonus: ₹3,00,000+ orders → 1% Extra Product
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Mobile Store Card */}
              <motion.div variants={fadeInUp} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="h-3" style={{ background: `linear-gradient(90deg, ${colors.freshGreen}, ${colors.darkGreen})` }}></div>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl text-white"
                      style={{ background: `linear-gradient(135deg, ${colors.freshGreen}, ${colors.darkGreen})` }}>
                      <FaTruck />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold" style={{ color: colors.darkGreen }}>Mobile Store</h3>
                      {/* <p className="text-lg font-bold" style={{ color: colors.freshGreen }}>6% Commission</p> */}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.freshGreen }}>POS System</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.freshGreen }}>Customer Billing</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.freshGreen }}>Delivery Boy</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-sm font-bold" style={{ color: colors.freshGreen }}>Stock Alert</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.freshGreen }} />
                      <span className="text-sm">Product Purchase (Pickup Center & Company)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.freshGreen }} />
                      <span className="text-sm">Customer Billing with GST</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.freshGreen }} />
                      <span className="text-sm">Delivery Boy Features & Tracking</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle style={{ color: colors.freshGreen }} />
                      <span className="text-sm">Sales & Profit Reports</span>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
                    <p className="text-sm font-bold text-center" style={{ color: colors.darkGreen }}>
                      Bonus: ₹35,000+ → 1% | ₹90,000+ → 2% Extra Product
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Platform Features Grid */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
                Platform Capabilities
              </h2>
              <p className="text-xl" style={{ color: colors.oliveBrown }}>
                Advanced technology for seamless business operations
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-4 max-w-6xl mx-auto">
              {[
                { icon: FaMobile, title: "3-Login System", desc: " Pickup center, Mobile Store, Member" },
                { icon: FaShieldAlt, title: "KYC Verified", desc: "PAN, Aadhaar, Bank verification" },
                { icon: FaQrCode, title: "OTP Verification", desc: "SMS/Email/WhatsApp" },
                { icon: FaFileInvoice, title: "GST Billing", desc: "Auto GST calculation" },
                { icon: FaHistory, title: "Weekly Payment Cycle", desc: "Timely payouts" },
                { icon: FaPercent, title: "5% TDS", desc: "Auto TDS deduction" },
                { icon: FaMapMarkedAlt, title: "Location Based", desc: "State, District, Tahsil, PIN" },
                { icon: FaCloudUploadAlt, title: "Real-time Updates", desc: "Daily BV updates" },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeInUp}
                  className="bg-slate-50 p-4 rounded-lg text-center hover:shadow-md transition"
                >
                  <item.icon className="text-3xl mx-auto mb-2" style={{ color: colors.darkBlue }} />
                  <h4 className="font-bold text-sm">{item.title}</h4>
                  <p className="text-xs mt-1" style={{ color: colors.oliveBrown }}>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Rank System Preview */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-20"
          >
            <div className="rounded-3xl p-8">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
                  17 Achievement Ranks
                </h2>
                <p className="text-xl" style={{ color: colors.oliveBrown }}>
                  Climb the ladder of success
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 max-w-5xl mx-auto">
                {[
                  { name: 'Starter',        img: RankStarter },
                  { name: 'Builder',        img: RankBuilder },
                  { name: 'Achiever',       img: RankAchiever },
                  { name: 'Performer',      img: RankPerformer },
                  { name: 'Grower',         img: RankGrower },
                  { name: 'Leader',         img: RankLeader },
                  { name: 'Star Leader',    img: RankStarLeader },
                  { name: 'Silver Icon',    img: RankSilverIcon },
                  { name: 'Gold Icon',      img: RankGoldIcon },
                  { name: 'Platinum Icon',  img: RankPlatinumIcon },
                  { name: 'Diamond Leader', img: RankDiamondLeader },
                  { name: 'Crown Diamond',  img: RankCrownDiamond },
                  { name: 'Royal Crown',    img: RankRoyalCrown },
                  { name: 'Global Crown',   img: RankGlobalCrown },
                  { name: 'Elite Royal',    img: RankEliteRoyal },
                  { name: 'Chairman Club',  img: RankChairmanClub },
                  { name: 'Legacy Founder', img: RankLegacyFounder },
                ].map((rank, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl text-center shadow-sm">
                    <img
                      src={rank.img}
                      alt={rank.name}
                      className="w-28 h-28 mx-auto mb-2 object-contain"
                    />
                    <div className="text-sm font-bold">{rank.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Reports & Analytics */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-20"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
              <motion.div variants={fadeInUp}>
                <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
                  Comprehensive Reports
                </h2>
                <p className="text-lg mb-6" style={{ color: colors.oliveBrown }}>
                  Track your business growth with detailed analytics
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    'Income Report',
                    'Level Wise Income',
                    'Monthly BV Report',
                    'GST Report',
                    'TDS Report',
                    'Team Report',
                    'Rank Progress',
                    'Wallet Statement'
                  ].map((report, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <FaCheckCircle style={{ color: colors.freshGreen }} />
                      <span className="text-sm">{report}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-slate-50 p-8 rounded-2xl">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg text-center">
                    <FaChartBar className="text-3xl mx-auto mb-2" style={{ color: colors.brightBlue }} />
                    <p className="text-xs font-bold">Daily BV Update</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg text-center">
                    <FaFileInvoice className="text-3xl mx-auto mb-2" style={{ color: colors.freshGreen }} />
                    <p className="text-xs font-bold">GST Reports</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg text-center">
                    <FaMoneyBillWave className="text-3xl mx-auto mb-2" style={{ color: colors.deepOrange }} />
                    <p className="text-xs font-bold">Payout Reports</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg text-center">
                    <FaUsers className="text-3xl mx-auto mb-2" style={{ color: colors.pinkMagenta }} />
                    <p className="text-xs font-bold">Team Analytics</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Compliance & Security */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="bg-slate-900 text-white rounded-3xl p-8"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">Compliance & Security</h2>
              <p className="text-slate-400">Fully compliant with Indian regulations</p>
            </div>

            <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="text-center">
                <FaFileInvoice className="text-4xl mx-auto mb-2" style={{ color: colors.lightOrange }} />
                <p className="text-sm font-bold">GST Bill</p>
                <p className="text-xs text-slate-400">On every purchase</p>
              </div>
              <div className="text-center">
                <FaPercent className="text-4xl mx-auto mb-2" style={{ color: colors.lightOrange }} />
                <p className="text-sm font-bold">5% TDS</p>
                <p className="text-xs text-slate-400">On withdrawal</p>
              </div>
              <div className="text-center">
                <FaHistory className="text-4xl mx-auto mb-2" style={{ color: colors.lightOrange }} />
                <p className="text-sm font-bold">7 Days Return</p>
                <p className="text-xs text-slate-400">Refund policy</p>
              </div>
              <div className="text-center">
                <FaShieldAlt className="text-4xl mx-auto mb-2" style={{ color: colors.lightOrange }} />
                <p className="text-sm font-bold">KYC Mandatory</p>
                <p className="text-xs text-slate-400">PAN, Aadhaar, Bank</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-20 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.navyBlue}, ${colors.darkBlue})`
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-white"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ready to Experience These Features?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: colors.lightOrange }}>
            Join ABM Group One today and unlock all these powerful features
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/register"
              className="px-10 py-4 bg-white rounded-lg font-bold text-lg hover:shadow-2xl transition transform hover:-translate-y-1"
              style={{ color: colors.navyBlue }}
            >
              Create Free Account
            </Link>
            <Link
              to="/contact"
              className="px-10 py-4 border-2 border-white rounded-lg font-bold text-lg hover:bg-white/20 transition"
            >
              Contact Sales
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm">
            <span className="flex items-center gap-2">
              <FaPhone /> 8887355992
            </span>
            <span className="flex items-center gap-2">
              <FaEnvelope /> support.abmgroupone@gmail.com
            </span>
            <span className="flex items-center gap-2">
              <FaWhatsapp /> 24/7 Support
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Features;